import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Building2,
  FileText,
  DollarSign,
  Clock,
} from "lucide-react";


export default function PaymentApprovalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Fetch payment details
  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const response = await axios.get(`${API_URL}/vendor-ap/payment-drafts/approve/${token}`);
        setDraft(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired approval link");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPayment();
    }
  }, [token]);

  // Handle approval action
  const handleAction = async (action) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/vendor-ap/payment-drafts/approve/${token}`,
        null,
        { params: { action, approver_name: "Board Member" } }
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to process approval");
    } finally {
      setProcessing(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white">Loading payment details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Unable to Load</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success/Result state
  if (result) {
    const isApproved = result.status === "sent_to_accounting";
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            {isApproved ? (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Payment Approved!</h2>
                <p className="text-slate-400 mb-4">
                  The payment has been approved and sent to accounting for processing.
                </p>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  {formatCurrency(draft?.total_amount)}
                </Badge>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-12 w-12 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Payment Rejected</h2>
                <p className="text-slate-400">
                  The payment request has been rejected.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already processed state
  if (draft?.message) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            <Clock className="h-16 w-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Already Processed</h2>
            <p className="text-slate-400">{draft.message}</p>
            <Badge className="mt-4" variant="outline">
              Status: {draft.status}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main approval view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <DollarSign className="h-8 w-8 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Payment Approval</h1>
          <p className="text-slate-400 mt-1">Review and approve this payment request</p>
        </div>

        {/* Amount Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-purple-600 border-0 mb-4">
          <CardContent className="pt-6 text-center">
            <p className="text-blue-100 text-sm mb-1">Total Amount</p>
            <p className="text-4xl font-bold text-white">
              {formatCurrency(draft?.total_amount)}
            </p>
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {draft?.draft?.items?.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                <div>
                  <div className="text-white font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    {item.vendor_name || "Vendor"}
                  </div>
                  <div className="text-sm text-slate-400">
                    Invoice: {item.invoice_number || "N/A"}
                  </div>
                </div>
                <div className="text-white font-bold">
                  {formatCurrency(item.amount)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            onClick={() => handleAction("reject")}
            disabled={processing}
            className="h-14 bg-red-600 hover:bg-red-700 text-white font-bold text-lg"
          >
            {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <>
                <XCircle className="h-5 w-5 mr-2" />
                Reject
              </>
            )}
          </Button>
          <Button
            onClick={() => handleAction("approve")}
            disabled={processing}
            className="h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg"
          >
            {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Approve
              </>
            )}
          </Button>
        </div>

        <p className="text-center text-slate-500 text-sm mt-4">
          By clicking Approve, you authorize this payment to proceed.
        </p>
      </div>
    </div>
  );
}
